import { createQueryHook } from '~/shared/providers/api';

export interface UserRolesMapResult {
  rolesMap: Record<string, string[]>;
  roleIdsMap: Record<string, string[]>;
}

/**
 * Query hook that fetches all user role assignments and builds a map
 * of userId -> unique role names. Used to enrich user records with
 * real role data in the users list.
 */
export const useUserRolesMapQuery = createQueryHook(
  'settings.teamPermissions.userRolesMap',
  () => ({
    coreUserRoleAssignments: {
      __args: {
        filter: {
          pagination: {
            limit: 10000,
            offset: 0,
          },
        },
      },
      entries: {
        userId: true,
        role: {
          id: true,
          name: true,
        },
      },
    },
  }),
  {
    select: (data): UserRolesMapResult => {
      const entries = data?.coreUserRoleAssignments?.entries ?? [];

      const userRoleNameSets = new Map<string, Set<string>>();
      const userRoleIdSets = new Map<string, Set<string>>();

      entries.forEach((assignment) => {
        const userId = assignment.userId;
        const roleName = assignment.role?.name;
        const roleId = assignment.role?.id;
        if (!userId) return;

        if (!userRoleNameSets.has(userId)) {
          userRoleNameSets.set(userId, new Set());
          userRoleIdSets.set(userId, new Set());
        }

        if (roleName) userRoleNameSets.get(userId)?.add(roleName);
        if (roleId) userRoleIdSets.get(userId)?.add(roleId);
      });

      const rolesMap: Record<string, string[]> = {};
      const roleIdsMap: Record<string, string[]> = {};
      userRoleNameSets.forEach((roles, userId) => {
        rolesMap[userId] = Array.from(roles);
      });
      userRoleIdSets.forEach((roleIds, userId) => {
        roleIdsMap[userId] = Array.from(roleIds);
      });

      return { rolesMap, roleIdsMap };
    },
  },
);
